/**
 * EmailTab — per-election invitation email: edit the text, see a live preview.
 * The preview is rendered by the server with sample recipient data and never
 * sends anything.
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Box, Card, Typography, TextField, Button, Chip, Switch, FormControlLabel,
  MenuItem, Alert, Snackbar, Stack, Divider, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import SendIcon from "@mui/icons-material/Send";
import RestoreIcon from "@mui/icons-material/Restore";
import api from "../../utils/api";

const cardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  backgroundColor: "rgba(255,255,255,0.96)",
  p: { xs: 2, md: 3 },
};
const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "10px" } };
const sectionTitleSx = { fontWeight: 700, color: "#223142", mb: 1.5 };

const ColorField = ({ label, value, onChange }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <input
      type="color"
      value={value || "#000000"}
      onChange={(e) => onChange(e.target.value)}
      style={{ width: 40, height: 32, border: "1px solid #c8d1dc", borderRadius: 6, padding: 2, background: "#fff", cursor: "pointer" }}
      aria-label={label}
    />
    <Typography variant="body2" sx={{ color: "#5a6578" }}>{label}</Typography>
  </Box>
);

const EmailTab = ({ election }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(null);
  const [meta, setMeta] = useState({ defaults: {}, tags: [], timezones: [], require_pin: false });
  const [preview, setPreview] = useState({ subject: "", html: "" });
  const [previewError, setPreviewError] = useState("");
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [testOpen, setTestOpen] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testSending, setTestSending] = useState(false);
  const [testError, setTestError] = useState("");

  const fieldRefs = useRef({});
  const lastFocused = useRef(null);
  const previewSeq = useRef(0);

  useEffect(() => {
    if (!election?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/elections/${election.id}/email-template`);
        if (cancelled) return;
        setForm(res.data.settings);
        setSaved(res.data.settings);
        setMeta({
          defaults: res.data.defaults,
          tags: res.data.tags || [],
          timezones: res.data.timezones || [],
          require_pin: !!res.data.require_pin,
        });
      } catch (err) {
        console.error("Failed to load email template:", err);
        setSnack({ open: true, message: "Failed to load the email template.", severity: "error" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [election?.id]);

  // Live preview (debounced) — rendered server-side with sample data
  useEffect(() => {
    if (!form || !election?.id) return;
    const t = setTimeout(async () => {
      const seq = ++previewSeq.current;
      try {
        const res = await api.post(`/elections/${election.id}/email-template/preview`, { settings: form });
        if (seq !== previewSeq.current) return;
        setPreview({ subject: res.data.subject, html: res.data.html });
        setPreviewError("");
      } catch (err) {
        if (seq !== previewSeq.current) return;
        setPreviewError(err.response?.data?.message || "Couldn't render the preview.");
      }
    }, 500);
    return () => clearTimeout(t);
  }, [form, election?.id]);

  const dirty = useMemo(
    () => !!form && !!saved && JSON.stringify(form) !== JSON.stringify(saved),
    [form, saved]
  );

  const setField = useCallback((key, value) => setForm((f) => ({ ...f, [key]: value })), []);

  const insertTag = (tag) => {
    const key = lastFocused.current;
    const el = key && fieldRefs.current[key];
    if (!key || !el) {
      setSnack({ open: true, message: "Click into a text field first, then pick a tag to insert.", severity: "info" });
      return;
    }
    const token = `{{${tag}}}`;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const next = el.value.slice(0, start) + token + el.value.slice(end);
    setField(key, next);
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    }, 0);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/elections/${election.id}/email-template`, { settings: form });
      setSaved(res.data.settings);
      setForm(res.data.settings);
      setSnack({ open: true, message: "Invitation email saved. New invitations will use it.", severity: "success" });
    } catch (err) {
      setSnack({ open: true, message: err.response?.data?.message || "Failed to save.", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const openTestDialog = () => {
    let last = "";
    try { last = localStorage.getItem("electionTestEmailTo") || ""; } catch (_) { /* storage unavailable */ }
    setTestTo((cur) => cur || last);
    setTestError("");
    setTestOpen(true);
  };

  const handleSendTest = async () => {
    const to = testTo.trim();
    if (!to || !to.includes("@")) {
      setTestError("Enter the email address to send the test to.");
      return;
    }
    setTestSending(true);
    setTestError("");
    try {
      const res = await api.post(`/elections/${election.id}/email-template/send-test`, { to, settings: form });
      try { localStorage.setItem("electionTestEmailTo", to); } catch (_) { /* storage unavailable */ }
      setTestOpen(false);
      setSnack({ open: true, message: res.data?.message || `Test email sent to ${to}`, severity: "success" });
    } catch (err) {
      setTestError(err.response?.data?.message || "The test email couldn't be sent.");
    } finally {
      setTestSending(false);
    }
  };

  const handleReset = () => {
    if (window.confirm("Reset all invitation email text and colors to the defaults? (Not saved until you click Save.)")) {
      setForm({ ...meta.defaults });
    }
  };

  const text = (key, label, opts = {}) => (
    <TextField
      key={key}
      label={label}
      value={form[key] ?? ""}
      onChange={(e) => setField(key, e.target.value)}
      onFocus={() => { lastFocused.current = key; }}
      inputRef={(el) => { fieldRefs.current[key] = el; }}
      size="small"
      fullWidth
      multiline={!!opts.rows}
      minRows={opts.rows}
      helperText={opts.helper}
      sx={fieldSx}
    />
  );

  if (loading || !form) {
    return <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={28} /></Box>;
  }

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) minmax(0, 1fr)" }, gap: 2.5, alignItems: "start" }}>
      <Card elevation={0} sx={cardSx}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, flexWrap: "wrap", mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>Invitation Email</Typography>
            <Typography variant="body2" sx={{ color: "#6b778c" }}>
              The text and look of the email sent with "Send Invitations" for this election.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button onClick={handleReset} startIcon={<RestoreIcon />} sx={{ textTransform: "none", fontWeight: 700 }}>
              Reset
            </Button>
            <Button
              variant="outlined"
              startIcon={<SendIcon />}
              onClick={openTestDialog}
              sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700 }}
            >
              Send test
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={!dirty || saving}
              sx={{ backgroundColor: "#2f63ad", borderRadius: "10px", textTransform: "none", fontWeight: 700, boxShadow: "none", "&:hover": { backgroundColor: "#275592", boxShadow: "none" } }}
            >
              {saving ? "Saving..." : dirty ? "Save" : "Saved"}
            </Button>
          </Stack>
        </Box>

        {!meta.require_pin && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            This election doesn't require a PIN, so lines that use <b>{"{{pin}}"}</b> are left out of the email.
            Turn on "Require PIN" in Settings to include each voter's personal PIN.
          </Alert>
        )}

        <Box sx={{ mb: 2.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#223142", mb: 0.75 }}>
            Insert a tag
          </Typography>
          <Typography variant="caption" sx={{ display: "block", color: "#6b778c", mb: 1 }}>
            Click into a text field, then click a tag to drop it in at the cursor. Each recipient gets their own values.
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {meta.tags.map((t) => (
              <Chip
                key={t.tag}
                label={`{{${t.tag}}}`}
                title={t.label}
                size="small"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => insertTag(t.tag)}
                sx={{ fontFamily: "monospace", fontWeight: 600, borderRadius: "8px", backgroundColor: "rgba(47,99,173,0.08)", color: "#2f63ad" }}
              />
            ))}
          </Box>
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        <Stack spacing={2.25}>
          <Typography variant="subtitle1" sx={sectionTitleSx}>Subject & banner</Typography>
          {text("subject", "Subject line")}
          {text("banner_title", "Banner text (top right)")}
          <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
            <ColorField label="Banner color (left)" value={form.header_color_start} onChange={(v) => setField("header_color_start", v)} />
            <ColorField label="Banner color (right)" value={form.header_color_end} onChange={(v) => setField("header_color_end", v)} />
            <ColorField label="Title color" value={form.heading_color} onChange={(v) => setField("heading_color", v)} />
          </Box>

          <Typography variant="subtitle1" sx={{ ...sectionTitleSx, mt: 1 }}>Message</Typography>
          {text("heading", "Title")}
          {text("greeting", "Greeting")}
          {text("headline", "Headline", { rows: 2 })}
          {text("intro", "Intro text", { rows: 3, helper: "One line per row." })}

          <Typography variant="subtitle1" sx={{ ...sectionTitleSx, mt: 1 }}>Steps</Typography>
          {text("steps_title", "Steps heading")}
          {text("steps", "Steps (one bullet per line)", {
            rows: 6,
            helper: "Use {{vote_link}} for the clickable link and {{pin}} for the voter's PIN. A line is left out if its tag has no value (for example {{pin}} when the election has no PIN).",
          })}
          {text("link_text", "Link text")}

          <Typography variant="subtitle1" sx={{ ...sectionTitleSx, mt: 1 }}>Deadline box</Typography>
          <FormControlLabel
            control={<Switch checked={!!form.show_callout} onChange={(e) => setField("show_callout", e.target.checked)} />}
            label="Show the deadline box next to the steps"
          />
          {form.show_callout && (
            <>
              {text("callout_label", "Deadline label")}
              {text("callout_text", "Deadline text", { helper: "{{end_date}} shows the voting end date and time." })}
            </>
          )}
          <TextField
            select
            size="small"
            label="Time zone for dates in the email"
            value={form.timezone}
            onChange={(e) => setField("timezone", e.target.value)}
            sx={fieldSx}
          >
            {meta.timezones.map((z) => <MenuItem key={z.value} value={z.value}>{z.label}</MenuItem>)}
          </TextField>

          <Typography variant="subtitle1" sx={{ ...sectionTitleSx, mt: 1 }}>Closing</Typography>
          {text("signature", "Signature", { rows: 3 })}
          {text("footer_note", "Note at the bottom", { rows: 2, helper: 'Starting it with "Note:" makes that word bold red.' })}
          <FormControlLabel
            control={<Switch checked={!!form.show_compliance_note} onChange={(e) => setField("show_compliance_note", e.target.checked)} />}
            label="Include the Florida statute notice (when this election has a compliance chapter set)"
          />
        </Stack>
      </Card>

      <Card elevation={0} sx={{ ...cardSx, position: { lg: "sticky" }, top: { lg: 16 } }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>Preview</Typography>
        <Typography variant="body2" sx={{ color: "#6b778c", mb: 1.5 }}>
          Sample owner and PIN. Nothing is sent from this page.
        </Typography>
        {previewError && <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 2 }}>{previewError}</Alert>}
        <Box sx={{ px: 1.5, py: 1, mb: 1.5, borderRadius: "10px", backgroundColor: "rgba(47,99,173,0.05)", fontSize: 13, color: "#223142" }}>
          <b>Subject:</b> {preview.subject || "…"}
        </Box>
        <Box
          component="iframe"
          title="Invitation email preview"
          sandbox=""
          srcDoc={preview.html}
          sx={{ width: "100%", height: { xs: 620, lg: 760 }, border: "1px solid rgba(47,99,173,0.12)", borderRadius: "12px", backgroundColor: "#fff" }}
        />
      </Card>

      <Dialog
        open={testOpen}
        onClose={() => !testSending && setTestOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: "#223142" }}>Send a test email</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "#5a6578", mb: 2 }}>
            Sends the invitation exactly as shown in the preview{dirty ? ", including your unsaved changes," : ""} to
            the address below, marked as a test. It uses a sample PIN and link, and no invitation is created.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            type="email"
            label="Send the test to"
            placeholder="you@example.com"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !testSending) handleSendTest(); }}
            disabled={testSending}
            sx={fieldSx}
          />
          {testError && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{testError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setTestOpen(false)} disabled={testSending} sx={{ textTransform: "none", fontWeight: 600, color: "#5a6578" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleSendTest}
            disabled={testSending}
            sx={{ backgroundColor: "#2f63ad", borderRadius: "10px", textTransform: "none", fontWeight: 700, boxShadow: "none", "&:hover": { backgroundColor: "#275592", boxShadow: "none" } }}
          >
            {testSending ? "Sending..." : "Send test"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((p) => ({ ...p, open: false }))} sx={{ borderRadius: "12px" }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmailTab;
